<template>
  <parent>
    <transition
      name="fade"
      mode="out-in"
    >
      <div v-if="paymentMethodsLoading || paymentsLoading">
        <loading-page />
      </div>
      <div
        v-else
        class="payment"
      >
        <div
          :class="
            currentBreakPoint === 'lg' || currentBreakPoint === 'xl'
              ? 'default-table'
              : 'mt-2'
          "
        >
          <div class="table-actions d-sm-flex justify-content-between">
            <h5 class="text-main">
              Payments
            </h5>
            <a class="link disabled">Create payment</a>
          </div>
          <div class="position-relative d-none d-lg-block">
            <transition-group
              name="fade"
              mode="out-in"
            >
              <b-table
                ref="table"
                key="table"
                :items="payments"
                :fields="fields"
                :class="{ loaded: !paymentsLoading }"
                show-empty
                responsive
              >
                <template #cell(amount)="data">
                  <div class="d-flex">
                    {{ getCurrency(data.item.currency)
                    }}{{ calcPrice(data.value) }}
                    <a
                      v-b-tooltip.hover.v-primary="'View payment details'"
                      class="link ml-auto"
                      @click="
                        $router.push({
                          name: 'payment-details',
                          params: { id: data.item.id },
                        })
                      "
                    >
                      <GjIcon name="ArrowRight" />
                    </a>
                  </div>
                </template>
                <template #cell(status)="data">
                  <span
                    class="text-capitalize tag"
                    :class="data.value"
                  >{{
                    formatStatus(data.value)
                  }}</span>
                </template>

                <template #cell(date)="data">
                  {{ formatOffsetDateTime(data.value) }}
                </template>

                <template #empty>
                  <transition
                    name="fade"
                    mode="out-in"
                  >
                    <div
                      v-if="fetchingPayments"
                      class="loading-table text-center py-3"
                    >
                      <b-spinner variant="primary" />
                    </div>
                    <div
                      v-else
                      class="d-flex justify-content-center align-items-center"
                      style="height: 220px"
                    >
                      No records.
                    </div>
                  </transition>
                </template>
              </b-table>
              <table-busy
                v-if="fetchingPayments && payments.length > 0"
                key="loading"
              />
            </transition-group>
          </div>
          <!-- MOBILE -->
          <div class="position-relative d-lg-none mt-1">
            <transition-group
              name="fade"
              mode="out-in"
            >
              <template v-for="card in payments">
                <card
                  :key="card.id"
                  :fields="fields"
                  :item="card"
                >
                  <template #amount>
                    {{ getCurrency(card.currency) }}{{ calcPrice(card.amount) }}
                    <a
                      v-b-tooltip.hover.v-primary="'View payment details'"
                      class="link"
                      @click="
                        $router.push({
                          name: 'payment-details',
                          params: { id: card.id },
                        })
                      "
                    >
                      <GjIcon name="ArrowRight" />
                    </a>
                  </template>
                  <template #status>
                    <span
                      class="text-capitalize tag"
                      :class="card.status"
                    >{{
                      formatStatus(card.status)
                    }}</span>
                  </template>

                  <template #date>
                    {{ formatOffsetDateTime(card.date) }}
                  </template>
                </card>
              </template>
              <template v-if="payments.length === 0">
                <paper-simple key="no data">
                  No records.
                </paper-simple>
              </template>
              <table-busy
                v-if="fetchingPayments"
                key="loading"
              />
            </transition-group>
          </div>
          <small-pagination
            v-if="paymentsResultInfo.hasMore || payments.length > paymentsLimit"
            :show-less="payments.length > paymentsLimit"
            :show-more="paymentsResultInfo.hasMore"
            class="mx-1 pb-1"
            @less="showPayments(true)"
            @more="showPayments(false)"
          />
        </div>
        <paper-simple class="mt-2">
          <div class="d-sm-flex justify-content-between">
            <h5 class="text-main">
              Payment method
            </h5>
            <a
              class="link"
              @click="showPopUp = true"
            >Add payment method</a>
          </div>
          <div class="mt-1">
            <table-busy v-if="fetchingPaymentMethods" />
            <template v-if="paymentMethods.length === 0">
              <div
                class="d-flex justify-content-center align-items-center"
                style="height: 220px"
              >
                No payment methods.
              </div>
            </template>
            <template v-else>
              <p>Automatically charge a payment method</p>
              <transition-group
                name="fade"
                mode="out-in"
              >
                <template v-for="card in paymentMethods">
                  <paper-simple
                    :key="card.id"
                    class="border"
                  >
                    <card-element
                      :card="card"
                      @delete="deleteCard"
                    />
                  </paper-simple>
                </template>
              </transition-group>
            </template>
            <small-pagination
              v-if="
                paymentMethodsResultInfo.hasMore ||
                  paymentMethods.length > paymentMethodsLimit
              "
              :show-less="paymentMethods.length > paymentMethodsLimit"
              :show-more="paymentMethodsResultInfo.hasMore"
              @less="showPaymentMethods(true)"
              @more="showPaymentMethods(false)"
            />
          </div>
        </paper-simple>
        <add-card
          v-if="showPopUp"
          @close="showPopUp = false"
          @cardAdded="cardAdded"
        />
      </div>
    </transition>
  </parent>
</template>
<script>
import redirectError from '@/services/redirectError'
import Card from '@/components/card/Card.vue'
import AddCard from '../AddCard.vue'
import CardElement from '../parts/CardElement.vue'
import SmallPagination from '../parts/SmallPagination.vue'
import currencies from '../currencies'

export default {
  name: 'Payments',
  components: {
    Card,
    AddCard,
    CardElement,
    SmallPagination,
  },
  data() {
    return {
      selectedCard: '',
      fields: [
        {
          key: 'amount',
          variant: 'small',
          isPrimary: true,
        },
        { key: 'status', variant: 'small', isPrimary: true },
        'description',
        { key: 'date', variant: 'time' },
      ],
      visible: false,
      showPopUp: false,
      currencies,
      paymentsLimit: 10,
      paymentMethodsLimit: 10,
      paymentMethodsLoading: true,
      paymentsLoading: true,
    }
  },
  computed: {
    fetchingPaymentMethods() {
      return this.$store.state.payments.fetchingPaymentMethods
    },
    paymentMethods() {
      return this.$store.state.payments.paymentMethods
    },
    fetchingPayments() {
      return this.$store.state.payments.fetchingPayments
    },
    payments() {
      return this.$store.state.payments.payments
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    paymentsResultInfo() {
      return this.$store.state.payments.paymentsResultInfo || {}
    },
    paymentMethodsResultInfo() {
      return this.$store.state.payments.paymentMethodsResultInfo || {}
    },
  },
  created() {
    this.$store
      .dispatch('payments/fetchPayments', { limit: this.paymentsLimit })
      .finally(() => {
        this.paymentsLoading = false
      })
    // .catch(err => redirectError(err, this.$router, this.$route.path))
    this.$store
      .dispatch('payments/fetchPaymentMethods', {
        limit: this.paymentMethodsLimit,
      })
      .finally(() => {
        this.paymentMethodsLoading = false
      })
    // .catch(err => redirectError(err, this.$router, this.$route.path))
  },
  methods: {
    showPayments(less) {
      if (less) {
        this.$store.commit('payments/RESET_PAYMENTS', this.paymentsLimit)
        return
      }
      const query = {
        limit: this.paymentsLimit,
        startingAfter: this.payments[this.payments.length - 1].id,
      }
      this.$store.dispatch('payments/fetchPayments', query)
    },

    showPaymentMethods(less) {
      if (less) {
        this.$store.commit(
          'payments/RESET_PAYMENT_METHODS',
          this.paymentMethodsLimit,
        )
        return
      }
      const query = {
        limit: this.paymentMethodsLimit,
        startingAfter: this.paymentMethods[this.paymentMethods.length - 1].id,
      }
      this.$store.dispatch('payments/fetchPaymentMethods', query)
    },

    cardAdded() {
      this.showPopUp = false
      this.$store.dispatch('payments/fetchPaymentMethods', {
        limit: this.paymentMethodsLimit,
      })
    },

    deleteCard(id) {
      this.confirmDelete(
        'Delete Card',
        'Are you sure you want to delete this card?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('payments/removePaymentMethod', id)
            .catch(err => redirectError(err, this.$router, this.$route.path))
        }
      })
    },

    getCurrency(curr) {
      const el = this.currencies.find(element => element.code === curr)
      return el ? el.symbol : curr
    },

    formatStatus(s) {
      return s.replaceAll('_', ' ')
    },

    calcPrice(val) {
      return (val / 100).toLocaleString()
    },
  },
}
</script>
<style lang="scss" scoped>
.tag {
  box-sizing: border-box;
  border-radius: 20px;
  padding: 2px 8px;
  color: var(--white);
  font-weight: bold;
  &.succeeded {
    background: var(--success);
    border: 1px solid var(--success);
  }
  &.processing {
    background: var(--primary);
    border: 1px solid var(--primary);
  }
  &.requires_action,
  &.requires_confirmation {
    background: var(--warning);
    border: 1px solid var(--warning);
  }
  &.requires_payment_method {
    background: var(--danger);
    border: 1px solid var(--danger);
  }
}

.disabled {
  opacity: 0.2;
  cursor: default;
}
</style>
